import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MemriseApiError, PathwaysApi } from 'src/api';
import { SessionType, WebSessionType } from 'src/api/learningSessions/types';
import { isEnglishLanguage } from 'src/utils/language/isEnglishLanguage';
import { getLocalStorageEntry, setLocalStorageEntry } from 'src/utils/webStorageUtils';

export const LEARNING_TOOLS_COACHMARK_DELAY_MS = 750;
export const LEARNING_TOOLS_COACHMARK_STORAGE_PREFIX = 'learning-tools-coachmark-seen';

interface UseLearningToolsCoachmarkProps {
  userId?: number;
  languagePairId: number;
  sourceLanguageLocale?: string;
  sessionType: WebSessionType;
  learnableId: number;
  isSecondLearnable: boolean;
  isPresentationCard: boolean;
  hasInitialMedia: boolean;
}

export const getLearningToolsCoachmarkStorageKey = (
  userId: number,
  languagePairId: number,
  skillLevelId: number,
) => [LEARNING_TOOLS_COACHMARK_STORAGE_PREFIX, userId, languagePairId, skillLevelId].join(':');

export const useLearningToolsCoachmark = ({
  userId,
  languagePairId,
  sourceLanguageLocale,
  sessionType,
  learnableId,
  isSecondLearnable,
  isPresentationCard,
  hasInitialMedia,
}: UseLearningToolsCoachmarkProps) => {
  const [shown, setShown] = useState(false);
  const [mediaCompletedLearnableId, setMediaCompletedLearnableId] = useState<number | null>(null);
  const isEligibleSession =
    sessionType === SessionType.LEARN && isEnglishLanguage(sourceLanguageLocale);

  const { data: activePathway } = useQuery({
    queryKey: ['activePathway', languagePairId],
    enabled: isEligibleSession,
    retry: false,
    queryFn: async () => {
      try {
        return await PathwaysApi.getActivePathway(languagePairId);
      } catch (error) {
        if (error instanceof MemriseApiError && error.httpStatusCode === 404) {
          return null;
        }
        throw error;
      }
    },
  });

  const storageKey = useMemo(() => {
    const skillLevelId = activePathway?.skillLevel.id;
    if (!userId || !skillLevelId) return null;
    return getLearningToolsCoachmarkStorageKey(userId, languagePairId, skillLevelId);
  }, [activePathway?.skillLevel.id, languagePairId, userId]);

  const initialMediaComplete = !hasInitialMedia || mediaCompletedLearnableId === learnableId;

  useEffect(() => {
    if (
      !isEligibleSession ||
      !isSecondLearnable ||
      !isPresentationCard ||
      !initialMediaComplete ||
      !storageKey ||
      getLocalStorageEntry(storageKey)
    ) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setLocalStorageEntry(storageKey, 'true');
      setShown(true);
    }, LEARNING_TOOLS_COACHMARK_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [initialMediaComplete, isEligibleSession, isPresentationCard, isSecondLearnable, storageKey]);

  const markInitialMediaComplete = useCallback(() => {
    setMediaCompletedLearnableId(learnableId);
  }, [learnableId]);

  const dismiss = useCallback(() => setShown(false), []);

  return {
    dismiss,
    markInitialMediaComplete,
    shown,
    storageKey,
  };
};
